import { getErrorMessage } from "./errorHandling";
import { capitalizeFirstLetterOfWords, packageNames } from "./utilityFunctions";

export type RewardStatus = "PENDING" | "CLAIMED" | "REDEEMED";
export type RewardActivityType = "REFERRAL" | "RESUME_COMPLETION" | "JOB_LEAD";

export interface Reward {
	_id: string;
	activityType: RewardActivityType;
	rewardStatus: RewardStatus;
	rewardPoints: number;
	initiatedDate?: string;
	claimedDate?: string | null;
	redeemedDate?: string | null;
	activityDetail?: string;
	referredFullName?: string;
	referredEmailAddress?: string;
	referredPhoneNumber?: string;
	resumeCompletionPct?: number;
	lastNotifiedOn?: string;
	jobTitle?: string;
	jobCompanyName?: string;
}

export interface PointsSummary {
	total: number;
	available: number;
	pending: number;
	redeemed: number;
}

/** The server will not release points until the resume behind them reaches this. */
export const CLAIMABLE_COMPLETION_PCT = 80;

/**
 * Completion of the resume a reward is waiting on, as a number.
 *
 * The server only attaches `resumeCompletionPct` when it found a resume journal for the address, so
 * an account whose referred friend has not started one — and a brand-new account, whose own
 * auto-created reward has nothing to measure yet — comes back with the field missing. Comparing
 * `undefined` against a threshold is false in both directions, which previously dropped those
 * rewards out of the list while still counting them in the totals.
 */
export function completionPctOf(reward: Reward): number {
	const pct = Number(reward.resumeCompletionPct);
	return Number.isFinite(pct) ? pct : 0;
}

export function isCollectable(reward: Reward): boolean {
	if (reward.rewardStatus !== "PENDING") return false;
	if (reward.activityType === "JOB_LEAD") return false;
	return completionPctOf(reward) >= CLAIMABLE_COMPLETION_PCT;
}

export interface Collectable {
	ids: string[];
	points: number;
}

/**
 * Every reward the server will release right now, and what they come to together.
 *
 * The wallet offers these as one action, so it needs the whole set rather than the first one: an
 * account that crossed the threshold on its own resume and on two referrals has three rewards to
 * collect and no reason to be asked three times.
 */
export function collectableRewards(rewards: Reward[]): Collectable {
	return rewards.filter(isCollectable).reduce<Collectable>(
		(collectable, reward) => {
			collectable.ids.push(reward._id);
			collectable.points += Number(reward.rewardPoints) || 0;
			return collectable;
		},
		{ ids: [], points: 0 },
	);
}

export function summarisePoints(rewards: Reward[]): PointsSummary {
	return rewards.reduce<PointsSummary>(
		(totals, reward) => {
			const points = Number(reward.rewardPoints) || 0;
			if (reward.rewardStatus === "CLAIMED") totals.available += points;
			if (reward.rewardStatus === "PENDING") totals.pending += points;
			if (reward.rewardStatus === "REDEEMED") totals.redeemed += points;
			totals.total += points;
			return totals;
		},
		{ total: 0, available: 0, pending: 0, redeemed: 0 },
	);
}

/** The reward every account gets for its own resume, created by the server on first read. */
export function findOwnResumeReward(rewards: Reward[]): Reward | undefined {
	return rewards.find((reward) => reward.activityType === "RESUME_COMPLETION");
}

export interface PendingBreakdown {
	/** Locked behind the reader's own resume reaching the threshold. */
	ownResume: number;
	/** Locked behind someone else's resume — the people they referred. */
	referrals: number;
}

/**
 * Split locked points by *whose* resume releases them.
 *
 * A single "points on the way" total hides the fact that these have different owners: finishing your
 * own resume releases only your share, and no amount of your effort releases the referral half. A
 * combined figure next to your own progress bar reads as a promise the page cannot keep.
 */
export function splitPendingPoints(rewards: Reward[]): PendingBreakdown {
	return rewards.reduce<PendingBreakdown>(
		(split, reward) => {
			if (reward.rewardStatus !== "PENDING") return split;
			const points = Number(reward.rewardPoints) || 0;
			if (reward.activityType === "RESUME_COMPLETION") split.ownResume += points;
			if (reward.activityType === "REFERRAL") split.referrals += points;
			return split;
		},
		{ ownResume: 0, referrals: 0 },
	);
}

/**
 * A typed-in name, tidied only where tidying cannot do harm.
 *
 * `capitalizeFirstLetterOfWords` lowercases the tail of every word, so running it over text that
 * already carries case turns "Maria McDonald" into "Maria Mcdonald". Names reach us however the
 * referrer typed them, so the fix is applied to the input that has no case of its own to lose —
 * "maria kamande" and "MARIA KAMANDE" — and anything mixed is left as written.
 */
export function titleCaseName(value?: string): string {
	const name = value?.trim();
	if (!name) return "";

	const carriesNoCase = name === name.toLowerCase() || name === name.toUpperCase();
	return carriesNoCase ? capitalizeFirstLetterOfWords(name) : name;
}

export function describeActivity(reward: Reward): string {
	if (reward.activityType === "REFERRAL")
		return titleCaseName(reward.referredFullName) || reward.referredEmailAddress || "A friend";
	if (reward.activityType === "RESUME_COMPLETION") return "Resume completion";
	return titleCaseName(reward.jobTitle) || "Shared opportunity";
}

/**
 * The address a reward is about.
 *
 * A referral names the person invited. The reward for the reader's own resume is about the reader,
 * and the server does not always send their address back on it, so the account supplies it.
 */
export function emailOnReward(reward: Reward, accountEmail?: string): string | undefined {
	if (reward.activityType === "RESUME_COMPLETION") return reward.referredEmailAddress || accountEmail;
	return reward.referredEmailAddress;
}

/**
 * What has to happen before these points can be spent, in the words of the person reading it.
 *
 * A bare `PENDING` never explained why the Collect button did nothing, which is the whole reason
 * someone lands here with points they cannot use.
 */
export function describeNextStep(reward: Reward): string {
	const pct = completionPctOf(reward);
	const remaining = CLAIMABLE_COMPLETION_PCT - pct;

	if (reward.rewardStatus === "REDEEMED") return "Spent on a subscription.";
	if (reward.rewardStatus === "CLAIMED") return "Ready to spend on a subscription.";

	if (reward.activityType === "JOB_LEAD") return "Waiting for the Dproz team to review this lead.";

	if (reward.activityType === "REFERRAL") {
		const name = titleCaseName(reward.referredFullName).split(" ")[0] || "your friend";
		// No "send them a reminder" here: the Remind button sits beside this text and says so itself.
		if (pct === 0) return `${name} has not started a resume yet.`;
		return `${name}'s resume is ${pct}% complete. ${remaining}% more and these points are yours.`;
	}

	if (pct === 0) return `Build your resume to ${CLAIMABLE_COMPLETION_PCT}% to collect these points.`;
	return `Your resume is ${pct}% complete. ${remaining}% more and these points are yours.`;
}

/** Where an invited person lands. Resume-first, because finishing one is what releases the points. */
const INVITE_PATH = "/resume/select-experience";

function firstNameOf(fullName?: string): string {
	return titleCaseName(fullName).split(" ")[0] || "there";
}

/** The message that goes out with a fresh invitation. */
export function referralInviteMessage(fullName?: string): string {
	return `Hi ${firstNameOf(fullName)}, I use Dproz to build my resume and find jobs. Build yours here and we both earn reward points: ${window.location.origin}${INVITE_PATH}`;
}

/**
 * The message that chases one that has already gone out.
 *
 * Someone who has started gets told how far they are, because a number they are close to beating is a
 * better reason to open the tab than a second copy of the original invitation.
 */
export function referralReminderMessage(reward: Reward): string {
	const name = firstNameOf(reward.referredFullName);
	const pct = completionPctOf(reward);
	const nudge =
		pct > 0
			? `your Dproz resume is ${pct}% done — finish it`
			: `I invited you to build a resume on Dproz — it takes a few minutes`;

	return `Hi ${name}, ${nudge} and we both earn reward points: ${window.location.origin}${INVITE_PATH}`;
}

/**
 * A WhatsApp hand-off, with or without a number.
 *
 * The phone number is optional on a referral, and `wa.me` treats an empty one as "ask me who" rather
 * than failing — so a referral saved without a number still gets a working button, it just opens the
 * contact picker. The old screen interpolated `undefined` straight into the path and produced a dead
 * link for exactly those referrals.
 */
export function whatsappLink(message: string, phoneNumber?: string): string {
	const digits = (phoneNumber || "").replace(/\D/g, "");
	return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

/** The server's own words for a reward, used wherever one is named so the two never disagree. */
export const STATUS_LABELS: Record<RewardStatus, string> = {
	PENDING: "Pending",
	CLAIMED: "Claimed",
	REDEEMED: "Redeemed",
};

/** How far along the resume behind a reward is, in the three states worth filtering by. */
export type CompletionBand = "none" | "partial" | "ready";

/**
 * Which band a reward falls in.
 *
 * A reward that has paid out reports no percentage of its own — the server stops tracking one once
 * the points are released — so it reads as ready rather than as a resume nobody has started.
 */
export function completionBandOf(reward: Reward): CompletionBand {
	if (reward.rewardStatus !== "PENDING") return "ready";

	const pct = completionPctOf(reward);
	if (pct >= CLAIMABLE_COMPLETION_PCT) return "ready";
	return pct > 0 ? "partial" : "none";
}

export interface RewardFilters {
	search: string;
	statuses: RewardStatus[];
	bands: CompletionBand[];
	types: RewardActivityType[];
}

export const NO_REWARD_FILTERS: RewardFilters = {
	search: "",
	statuses: [],
	bands: [],
	types: [],
};

export function countActiveFilters(filters: RewardFilters): number {
	return filters.statuses.length + filters.bands.length + filters.types.length + (filters.search.trim() === "" ? 0 : 1);
}

/** Everything on the row that someone could reasonably type to find it again. */
function searchableText(reward: Reward): string {
	return [
		describeActivity(reward),
		reward.referredEmailAddress,
		reward.referredPhoneNumber,
		reward.jobCompanyName,
		reward.activityDetail,
		STATUS_LABELS[reward.rewardStatus],
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
}

/**
 * The rows that survive every filter.
 *
 * An empty set of boxes means every value passes, so opening a menu and picking nothing narrows
 * nothing. The nested ternary this replaces let a checked status swallow the search term, so typing
 * a name while filtering by status returned rows that matched neither.
 */
export function filterRewards(rewards: Reward[], filters: RewardFilters): Reward[] {
	const search = filters.search.trim().toLowerCase();

	return rewards.filter((reward) => {
		if (filters.statuses.length > 0 && !filters.statuses.includes(reward.rewardStatus)) return false;
		if (filters.types.length > 0 && !filters.types.includes(reward.activityType)) return false;
		if (filters.bands.length > 0 && !filters.bands.includes(completionBandOf(reward))) return false;
		return search === "" || searchableText(reward).includes(search);
	});
}

export const TIER_ORDER = ["BRONZE", "SILVER", "GOLD", "PLATINUM"] as const;
export type TierName = (typeof TIER_ORDER)[number];

/** Named handles for the tiers, e.g. `SUBSCRIPTION_TIERS.GOLD`. Derived from `TIER_ORDER` so the two never drift. */
export const SUBSCRIPTION_TIERS = Object.fromEntries(TIER_ORDER.map((tier) => [tier, tier])) as {
	[K in TierName]: K;
};

export function toTierName(value?: string): TierName {
	const upper = (value || "").toUpperCase() as TierName;
	return TIER_ORDER.includes(upper) ? upper : "BRONZE";
}

export interface NextTierProgress {
	tier: TierName;
	/** The most points this tier will accept against its price. */
	maxDiscountPoints: number;
	price: number;
	/** Collected points that count toward that cap. */
	earned: number;
	remaining: number;
	pct: number;
}

/**
 * How far collected points go toward the next tier up.
 *
 * The target is that tier's `maxRedeemablePoints`, not its price: points offset a plan 1 TZS each up
 * to a per-plan cap and never buy one outright. Measuring progress against the full price would
 * draw a bar that can never fill, and imply a free upgrade the checkout will not honour.
 */
export function nextTierProgress(current: TierName, availablePoints: number): NextTierProgress | null {
	const next = TIER_ORDER[TIER_ORDER.indexOf(current) + 1];
	if (!next) return null;

	const plan = packageNames[next.toLowerCase()];
	if (!plan) return null;

	const maxDiscountPoints = plan.maxRedeemablePoints;

	return {
		tier: next,
		maxDiscountPoints,
		price: plan.originalAmount,
		earned: Math.min(availablePoints, maxDiscountPoints),
		remaining: Math.max(0, maxDiscountPoints - availablePoints),
		pct: maxDiscountPoints > 0 ? Math.min(100, (availablePoints / maxDiscountPoints) * 100) : 0,
	};
}

export interface Subscription {
	_id?: string;
	package?: string;
	status?: string;
	paymentStatus?: string;
	expireAt?: string;
	payments?: Array<{
		amount: number;
		paidAt: string;
		confirmationCode: string;
	}>;
}

/**
 * The subscription that decides what the account can do today: the one that runs longest.
 *
 * Mirrors the rule the settings page has always used, so both surfaces name the same package.
 */
export function chooseSubscription(payments: Subscription[]): Subscription | null {
	if (!Array.isArray(payments) || payments.length === 0) return null;

	return payments.reduce((longest, candidate) =>
		new Date(candidate.expireAt ?? 0) > new Date(longest.expireAt ?? 0) ? candidate : longest,
	);
}

export function isSubscriptionActive(subscription: Subscription | null): boolean {
	if (!subscription?.expireAt) return false;
	return new Date(subscription.expireAt) > new Date();
}

/** The one refusal the referral endpoint sends, whatever the reason behind it. */
const REFERRAL_ALREADY_EXISTS = /reward activity already exists/i;

/**
 * Why a referral was turned down, in the reader's terms.
 *
 * The server answers every duplicate with the same sentence — `Reward activity already exists: Your
 * Friend [Name] Is On Dproz!` — for three causes it does not tell apart: the address already has an
 * account or a resume, somebody has already referred it, or it is the reader's own. This wording has
 * to hold for all three, so it names the outcome and the way out rather than guessing which happened.
 * Anything else the endpoint says is passed through, since only this one message is known to be ours.
 */
export function referralRefusalMessage(error: unknown, referredName?: string): string {
	const message = getErrorMessage(error, "");
	if (!message) return "We could not send that referral. Please try again.";
	if (!REFERRAL_ALREADY_EXISTS.test(message)) return message;

	const who = titleCaseName(referredName) || "That email address";
	return `${who} is already on Dproz, or has been referred before, try a different email address.`;
}
